import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrderStatus, redirectToCardPayment, type EpayQrInfo, type PaymentInitResponse } from '../api/billing';

const QR_POLL_INTERVAL_MS = 3000;

/**
 * Shared handling for whatever /billing/checkout or /subscriptions/subscribe
 * return: redirect to the ePay hosted page (card), open+poll the QR modal, or
 * — for fulfilled zero-amount orders — report success immediately.
 *
 * Used by both PlansPage and ProfilePage so every "buy" button in the app
 * goes through the real ePay flow instead of assuming an instant top-up.
 */
export function useEpayPayment(onPaid: () => void, onFailed: () => void) {
  const navigate = useNavigate();
  const [qrState, setQrState] = useState<{ invoiceId: string; qr: EpayQrInfo } | null>(null);
  const [qrPolling, setQrPolling] = useState(false);

  useEffect(() => {
    if (!qrState) return;
    let cancelled = false;
    let inFlight = false;
    const tick = async () => {
      if (inFlight) return;
      inFlight = true;
      setQrPolling(true);
      try {
        const status = await getOrderStatus(qrState.invoiceId);
        if (cancelled) return;
        if (status.status === 'paid') {
          setQrState(null);
          if (status.fulfilled === false) navigate(`/payment/success?invoice_id=${encodeURIComponent(qrState.invoiceId)}`);
          else onPaid();
        } else if (status.status === 'failed') {
          setQrState(null);
          onFailed();
        }
      } catch {
        // transient network error — keep polling silently
      } finally {
        inFlight = false;
        if (!cancelled) setQrPolling(false);
      }
    };
    const interval = setInterval(tick, QR_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [qrState]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePaymentInit = useCallback(async (init: PaymentInitResponse): Promise<void> => {
    if (init.already_paid) {
      if (init.fulfilled === false) navigate(`/payment/success?invoice_id=${encodeURIComponent(init.invoice_id)}`);
      else onPaid();
      return;
    }
    if (init.method === 'qr' && init.qr) {
      setQrState({ invoiceId: init.invoice_id, qr: init.qr });
      return;
    }
    if (init.method === 'card' && init.payment) {
      await redirectToCardPayment(init.payment);
      return;
    }
    throw new Error('epay_bad_response');
  }, [onPaid, navigate]);

  return { qrState, qrPolling, handlePaymentInit, closeQr: () => setQrState(null) };
}
